import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState } from "react";
import {
  getRequests,
  deleteRequest,
  updateRequest,
} from "../../services/api/getAll";

const statusMap: Record<number, string> = {
  1: "Pending",
  2: "Rejected",
  3: "Reviewed",
};

const statusOptions = [
  { label: "Pending", value: 1 },
  { label: "Rejected", value: 2 },
  { label: "Reviewed", value: 3 },
];

const RequestsAdminScreen = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchRequests = async () => {
    try {
      const res = await getRequests();
      setRequests(res?.items || []);
    } catch (e) {
      console.error("Failed to fetch requests", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleStatusChange = async (id: number, status: number) => {
    setUpdatingId(id);
    try {
      await updateRequest({ id, status });
      fetchRequests();
    } catch (e) {
      console.error("Failed to update status", e);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteRequest(id);
      setRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error("Delete failed", e);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 6 },
        bgcolor: "#f9fafb",
        minHeight: "100vh",
        fontFamily: "Poppins",
      }}
    >
      <Typography
        variant="h3"
        fontWeight="bold"
        color="#1e3a8a"
        mb={4}
        sx={{ fontSize: { xs: "28px", md: "36px" } }}
      >
        Job Applications
      </Typography>

      {loading ? (
        <Box
          height="70vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={40} />
        </Box>
      ) : (
        requests.map((request) => (
          <Paper
            key={request.id}
            elevation={3}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 4,
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              position: "relative",
              transition: "0.3s",
              "&:hover": {
                boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={2}
            >
              <Box>
                <Typography fontWeight="600" fontSize="20px" color="#0f172a">
                  {request.fullName}
                </Typography>
                <Typography fontSize="14px" color="#64748b" mt={0.5}>
                  {request.email} • {request.phoneNumber}
                </Typography>
              </Box>
              <IconButton
                color="error"
                onClick={() => handleDelete(request.id)}
              >
                {deletingId === request.id ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
            </Box>

            <Typography fontSize="15px" color="#334155" mb={1}>
              <strong style={{ color: "#0f172a" }}>Message:</strong>{" "}
              {request.message}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              href={request.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                mb: 2,
                borderRadius: 2,
                borderColor: "#0ea5e9",
                color: "#0ea5e9",
                "&:hover": {
                  backgroundColor: "#e0f2fe",
                  borderColor: "#0284c7",
                  color: "#0284c7",
                },
              }}
            >
              View File
            </Button>

            <Typography fontSize="14px" color="#475569" mb={2}>
              <strong style={{ color: "#0f172a" }}>Submitted:</strong>{" "}
              {new Date(request.createdAt).toLocaleString()}
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              <Typography fontWeight="600" color="#0f172a">
                Status:
              </Typography>
              <Select
                size="small"
                value={request.status}
                onChange={(e) =>
                  handleStatusChange(request.id, Number(e.target.value))
                }
                disabled={updatingId === request.id}
                sx={{
                  minWidth: 150,
                  backgroundColor: "#f1f5f9",
                  borderRadius: 2,
                  "& .MuiSelect-select": {
                    fontWeight: 500,
                    color: "#0f172a",
                  },
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {updatingId === request.id && <CircularProgress size={18} />}
            </Box>
          </Paper>
        ))
      )}
    </Box>
  );
};

export default RequestsAdminScreen;
